import type { LocaleCode } from "@homeinstead/locale";
import type {
  LeadFlowAnswerFieldName,
  LeadFlowAnswers,
  LeadFlowDocument,
  LeadFlowStepDefinition,
  LeadFlowStepErrors,
} from "../types";

import {
  getLeadFlowQuestionFieldNames,
  getRequiredLeadFormFieldNames,
  isLeadFormDeliveryContext,
  LEAD_FORM_TYPE,
} from "@homeinstead/forms/lead-form";
import { CONTRACT_SHAPE } from "../constants/contract-shape";
import { getPostalLocationValidationErrors } from "./get-postal-location-validation-errors";

type GetLeadFlowStepErrorsInput = {
  answers: LeadFlowAnswers;
  deliveryContext: LeadFlowDocument["deliveryContext"];
  localeCode: LocaleCode;
  step: LeadFlowStepDefinition;
};

function getFieldError(
  fieldName: LeadFlowAnswerFieldName,
  value: unknown,
): string | undefined {
  // An unanswered text field validates as empty so the visitor sees the
  // schema's "required" message rather than a type error.
  const result = CONTRACT_SHAPE[fieldName]?.safeParse(value ?? "");

  return result && !result.success
    ? result.error.issues[0]?.message
    : undefined;
}

/**
 * Errors that block Continue on a step: every contract-required field
 * the step asks must be answered and valid. Fields the contract leaves
 * optional (`careFor`, `careType`, `urgency`) can be skipped. The postal code
 * keeps the legacy rules, so a typed code also needs a picked office.
 */
function getStepErrors({
  answers,
  deliveryContext,
  localeCode,
  step,
}: GetLeadFlowStepErrorsInput): LeadFlowStepErrors {
  if (
    step._type !== "leadFlowStep" ||
    !isLeadFormDeliveryContext(deliveryContext)
  ) {
    return {};
  }

  const requiredFieldNames = new Set(
    getRequiredLeadFormFieldNames({
      deliveryContext,
      formType: LEAD_FORM_TYPE.careSeeker,
    }),
  );
  const errors: LeadFlowStepErrors = {};

  for (const fieldName of (step.items ?? []).flatMap((item) =>
    getLeadFlowQuestionFieldNames(item),
  )) {
    if (!requiredFieldNames.has(fieldName)) {
      continue;
    }

    if (fieldName === "postalCode") {
      Object.assign(
        errors,
        getPostalLocationValidationErrors(
          {
            location: answers.location ?? "",
            postalCode: answers.postalCode ?? "",
          },
          localeCode,
        ),
      );
      continue;
    }

    const message = getFieldError(
      fieldName as LeadFlowAnswerFieldName,
      answers[fieldName as LeadFlowAnswerFieldName],
    );
    if (message) {
      errors[fieldName] = [message];
    }
  }

  return errors;
}

export { getStepErrors };
